import { ref, onMounted, onBeforeUnmount, Ref, nextTick } from "vue";

export const useScrollToTop = (containerRef: Ref<HTMLElement | Window>) => {
  const isScrollable = ref(false);
  const isAtTop = ref(true);

  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null; // API 호출 후 데이터 렌더링 감지

  const updateScrollStatus = () => {
    let scrollHeight: number, clientHeight: number, scrollTop: number;

    if (containerRef.value instanceof HTMLElement) {
      scrollHeight = containerRef.value.scrollHeight;
      clientHeight = containerRef.value.clientHeight;
      scrollTop = containerRef.value.scrollTop;
    } else {
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    }

    isScrollable.value = scrollHeight > clientHeight;
    isAtTop.value = scrollTop <= 0;
  };

  onMounted(async () => {
    await nextTick();
    updateScrollStatus();

    const el =
      containerRef.value instanceof HTMLElement ? containerRef.value : window;

    // 스크롤 / 리사이즈 이벤트
    el.addEventListener("scroll", updateScrollStatus);
    window.addEventListener("resize", updateScrollStatus);

    // 동적 컨텐츠 변경(자식 엘리먼트 추가/삭제) 감지
    if (containerRef.value instanceof HTMLElement) {
      mutationObserver = new MutationObserver(() => {
        // 자식 변동 있을 때마다 높이를 재계산
        updateScrollStatus();
      });
      mutationObserver.observe(containerRef.value, {
        childList: true,
        subtree: true,
      });

      resizeObserver = new ResizeObserver(updateScrollStatus);
      resizeObserver.observe(containerRef.value);
    }
  });

  onBeforeUnmount(() => {
    const el =
      containerRef.value instanceof HTMLElement ? containerRef.value : window;

    el.removeEventListener("scroll", updateScrollStatus);
    window.removeEventListener("resize", updateScrollStatus);

    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
  });

  const scrollToTop = (el: HTMLElement | Window) => {
    if (el instanceof HTMLElement) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return { isScrollable, isAtTop, scrollToTop };
};
